import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../../app/hooks';
import { rG_setProject } from '../../app/global-slice';
import { setSelectedTool } from '../../app-slice';
import { handleEvent } from '../../app-actions';
import {
  APIStatus,
  APIStatusItem,
  CavernFileType,
  ComputeServiceType,
  DataItemType,
  JobType,
  Project,
  ToolType,
  toolTypeToPath,
} from '../../utils/types';
//import { APIPrefix } from '../../utils/functions';
import { Event, EventType, EventTarget, APIStatusParams } from '../../utils/events';
import {
  Folder,
  CheckCircle,
  AlertTriangle,
  Database,
  Cpu,
  Monitor,
  ArrowRightLeft,
  Search,
  FileText,
  LayoutDashboard,
  Package,
  BookOpen,
  Eye,
  ExternalLink,
} from 'lucide-react';
import './Landing.css';
import HeroSection from '../../components/dashboard/HeroSection';
import ProjectCard from '../../components/dashboard/ProjectCard';
import ServiceHealth from '../../components/dashboard/ServiceHealth';
import PleaseWait from '../../tools/please-wait';

export default function Landing() {
  // translation function
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const sG_DarkMode: boolean | undefined = useAppSelector(
    (state) => state.global.darkMode,
  );
  const sG_LoggedIn: boolean | undefined = useAppSelector(
    (state) => state.global.loggedIn,
  );
  const sG_PreferredUsername: string | undefined = useAppSelector(
    (state) => state.global.preferredUsername,
  );
  const sG_Username: string | undefined = useAppSelector(
    (state) => state.global.username,
  );
  let _firstName: string | undefined = undefined;
  if (sG_Username !== undefined) {
    if (sG_Username.indexOf(' ') > -1) {
      _firstName = sG_Username.substring(0, sG_Username.indexOf(' '));
    }
  }
  const sG_Project: Project | undefined = useAppSelector((state) => state.global.project);
  const sProjects: Project[] = useAppSelector((state) => state.data.projects);
  // the data items displayed in the table.
  const sProjectItems: DataItemType[] = useAppSelector(
    (state) => state.project.projectItems,
  );
  const sCavernItems: CavernFileType[] = useAppSelector(
    (state) => state.project.userItems,
  );
  const sLoadingProjects = useAppSelector((state) => state.app.loadingProjects);
  const sAPIStatuses: APIStatusItem[] = useAppSelector(
    (state) => state.home.sAPIStatuses,
  );
  // get a list of active sessions.
  const sSessions: ComputeServiceType[] = useAppSelector(
    (state) => state.interactiveSessions.sessions,
  );
  const sActiveSessions: ComputeServiceType[] = sSessions.filter(
    (session: ComputeServiceType) =>
      session.resource_type === 'canfar' &&
      ['available', 'failed', 'error', 'expired'].includes(session.compute_status) ===
        false,
  );
  // get a list of active data staging requests.
  const sStagingJobs: JobType[] = useAppSelector(
    (state) => state.fileBrowser.dataManagementJobs,
  );
  const sActiveStagingJobs: JobType[] = sStagingJobs.filter((job: JobType) =>
    ['preparing', 'staging', 'retrying'].includes(job.status),
  );
  const displayName = _firstName || sG_Username;
  const numOfFilesInProject = sProjectItems.length + sCavernItems.length;

  const selectProject = (project: Project) => {
    const selectedProject = sProjects.find((p: Project) => p.name === project.name);

    if (!selectedProject) {
      return;
    }
    dispatch(rG_setProject(selectedProject));
    dispatch(setSelectedTool(ToolType.PROJECT));
    navigate(toolTypeToPath[ToolType.PROJECT]);
  };

  return (
    <div className="landing" data-theme={sG_DarkMode ? 'dark' : 'light'}>
      {/* System health banner */}
      {/* <ServiceHealth apiStatuses={sAPIStatuses} /> */}
      {/* Hero */}
      <HeroSection
        loggedIn={sG_LoggedIn}
        displayName={displayName}
        selectedProject={sG_Project}
        metrics={[
          {
            value: sActiveSessions.length,
            label: 'active session(s)',
          },
          {
            value: sActiveStagingJobs.length,
            label: 'data item(s) being moved',
          },
        ]}
      />

      {/* Projects */}
      <section className="landing-projects-section">
        <div className="landing-section-heading">
          <span className="landing-section-title">
            {sG_LoggedIn &&
              t(
                'You are a member of the following projects. Please select a project to work with:',
              )}
          </span>

          {sG_LoggedIn && !sLoadingProjects && (
            <span className="landing-section-count">{sProjects.length} available</span>
          )}
        </div>

        {sLoadingProjects && (
          <div style={{ flex: '0 0 auto', margin: '10px 0px 10px 10px', width: '90%' }}>
            <PleaseWait text={t('Loading, please wait') + '.....'} visible={true} />
          </div>
        )}

        {!sLoadingProjects && sProjects.length === 0 && (
          <div className="landing-empty">
            <div className="landing-empty-icon">
              <Folder size={40} />
            </div>

            <p className="landing-empty-title">{t('No projects found')}</p>

            <p className="landing-empty-text">
              {sG_LoggedIn
                ? 'You have no projects assigned. Contact your administrator.'
                : 'Log in to see your projects.'}
            </p>
          </div>
        )}

        {!sLoadingProjects && sProjects.length > 0 && (
          <div className="landing-projects-grid">
            {sProjects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                numOfFilesInProject={numOfFilesInProject}
                selected={sG_Project?.name === project.name}
                onSelect={selectProject}
              />
            ))}
          </div>
        )}
      </section>

      {/* Quick access */}
    </div>
  );
}
